import React from "react";
import PackageDetailsStyles from "./PackageDetailsCard.styles";
import { Badge, OverlayTrigger, Stack } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxOpen, faSpinner } from "@fortawesome/free-solid-svg-icons";
import CardUtils from "../Card.utils";
import PHASE_LIST from "./PhaseList/PhaseList.component";
import THEME from "../../../theme/index";
import TIMING_LIST from "./TimingList/TimingList.component";
import CONTACT_LIST from "./ContactsList/ContactList.component";
import { isNullOrUndefined } from "../../../shared/utils/data.utils";
import { PackageDetailsContacts } from "../../../api/models";
import { isMobile } from "react-device-detect";
import {
  EPhaseNames,
  EPhaseStates,
  parsePhaseName,
} from "../../../shared/enums/phases.enums";
import { IPackageDetailsCardProps } from "./PackageDetailsCard.types";
import {
  IdNamePairVM,
  PackageDetailsPhaseVM,
  PackageDetailsTimingVM,
} from "../../../api/generated/models";
import TOOL_TIP from "../../tooltip/ToolTip.component";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";

const PACKAGE_DETAILS_CARD: React.FC<IPackageDetailsCardProps> = (
  props: IPackageDetailsCardProps
) => {
  let contacts: PackageDetailsContacts | undefined;
  let status: IdNamePairVM | undefined;
  let phases: Array<PackageDetailsPhaseVM> | null | undefined;
  let timing: PackageDetailsTimingVM | undefined;
  let packageName: string | null | undefined;

  let currentPhaseName: EPhaseNames | undefined;

  const sendDataToGTM = useGTMDispatch();
  const { subscription, projectSubscription } = props;
  const { isLoading, isError } = subscription;

  if (subscription.data) {
    const { data } = subscription;

    ({ contacts } = data);
    ({ status } = data);
    ({ phases } = data);
    ({ timing } = data);
    ({ packageName } = data);
  }

  const badgeColorFlag = CardUtils.getBadgeFormat(status?.name ?? "").colorFlag;
  const badgeLabel = CardUtils.getBadgeFormat(status?.name ?? "").label;
  const currentPhase: PackageDetailsPhaseVM | undefined = phases?.find(
    (element) => element.state?.id == EPhaseStates.ACTIVE
  );

  // istanbul ignore next
  if (currentPhase?.phase?.name) {
    currentPhaseName =
      parsePhaseName(currentPhase?.phase?.name, true) ?? undefined;
  }

  return (
    <PackageDetailsStyles.PackageDetailsCard
      className="package-details-card"
      data-testid="project-details-card"
    >
      {isLoading && !isError && (
        <div
          className="spinner-container"
          data-testid="package-details-spinner"
        >
          <FontAwesomeIcon
            icon={faSpinner}
            className="fa-spin"
            size="3x"
            color="#666666"
          />
        </div>
      )}
      {/* @todo: As the data loads, there are occasions where errors
           are visible before the data actually loads. Need to look into
           this and We will need to decide how we want to handle API errors.
      */}
      {/* { isError && !isLoading && <div>{JSON.stringify(error)}</div>} */}
      {!isLoading &&
        !isError &&
        !projectSubscription.isLoading &&
        !projectSubscription.isError &&
        !isNullOrUndefined(phases) &&
        !isNullOrUndefined(timing) &&
        !isNullOrUndefined(contacts) && (
          <>
            <PackageDetailsStyles.PackageDetailsHeader
              className="package-details-header"
              data-testid="package-details-header"
            >
              <Stack direction="horizontal" gap={3}>
                <FontAwesomeIcon
                  className="package-icon"
                  icon={faBoxOpen}
                  color={THEME.CE_THEME.colors.grays.septenary}
                />

                {
                  /* istanbul ignore next */ !isMobile && (
                    <>
                      <h4>{packageName || ""}</h4>
                      <div className="ms-auto">
                        <OverlayTrigger
                          trigger="click"
                          rootClose
                          transition
                          placement="left"
                          onToggle={(nextShow: boolean) => {
                            if (nextShow) {
                              sendDataToGTM({
                                event: "eventTracking",
                                category: "Construction",
                                action: `Tooltip Opened - ${badgeLabel} Badge`,
                                label: "Project Details - Header Badge",
                              });
                            }
                          }}
                          overlay={TOOL_TIP({
                            packageStatus: status?.name ?? "",
                          })}
                        >
                          <Badge className="badge-tooltip" bg={badgeColorFlag}>
                            {badgeLabel}
                          </Badge>
                        </OverlayTrigger>
                      </div>
                    </>
                  )
                }
                {
                  /* istanbul ignore next */ isMobile && (
                    <Stack className="mobile-stack" direction="vertical">
                      <h4>{packageName || ""}</h4>
                      <div>
                        <OverlayTrigger
                          trigger="click"
                          rootClose
                          transition
                          placement="bottom"
                          onToggle={(nextShow: boolean) => {
                            if (nextShow) {
                              sendDataToGTM({
                                event: "eventTracking",
                                category: "Construction",
                                action: `Tooltip Opened - ${badgeLabel} Badge`,
                                label: "Project Details - Header Badge",
                              });
                            }
                          }}
                          overlay={TOOL_TIP({
                            packageStatus: status?.name ?? "",
                          })}
                        >
                          <Badge className="badge-tooltip" bg={badgeColorFlag}>
                            {badgeLabel}
                          </Badge>
                        </OverlayTrigger>
                      </div>
                    </Stack>
                  )
                }
              </Stack>
            </PackageDetailsStyles.PackageDetailsHeader>
            <PackageDetailsStyles.PackageDetailsBody
              className="package-details-body"
              data-testid="package-details-body"
            >
              <PHASE_LIST phases={phases} />
              <TIMING_LIST timing={timing} phase={currentPhaseName} />
              <CONTACT_LIST
                contacts={contacts}
                project={{
                  projectId: projectSubscription.data?.projectId ?? "",
                  projectTitle: projectSubscription?.data?.projectTitle ?? "",
                }}
              />
            </PackageDetailsStyles.PackageDetailsBody>
          </>
        )}
    </PackageDetailsStyles.PackageDetailsCard>
  );
};

export default PACKAGE_DETAILS_CARD;
